import React, { useState, useEffect, useRef } from 'react'
import { SelectBase } from 'components/forms/Selects/SelectBase'
import { IAutocompleteBaseOption } from 'transfix-ui/components/AutocompleteBase'
import AutocompleteShipper from 'transfix-ui/components/AutocompleteShipper/AutocompleteShipper'
import { IconButton } from 'transfix-ui/components/IconButton'
import makeStyles from 'transfix-ui/core/styles/makeStyles'
import { brandColors } from 'transfix-ui/theme/colors'
import { DownArrowIcon, CloseIcon } from 'transfix-icons'

interface IShipperFilterButtonProps {
  label: string
  dataTest: string
  selectShipperHandler: (newVal: TBaseValue | null) => void
  clearShipperHandler: () => void
  shipper: TBaseValue | null
}

const useStyles = makeStyles(theme => ({
  actionIcon: {
    display: 'flex',
    color: brandColors.skyBlue6,
  },
  textContainer: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
  },
  prefixIcon: {
    paddingRight: theme.spacing(1),
    minHeight: 20,
    position: 'absolute',
    color: brandColors.black,
  },
  text: {
    color: brandColors.black,
    whiteSpace: 'nowrap',
    paddingRight: theme.spacing(1),
  },
  parent: {
    display: 'inline',
  },
  searchBox: {
    margin: '12px',
  },
}))

type TBaseValue = Pick<IAutocompleteBaseOption, 'id' | 'text'>

export const ShipperFilterButton = ({
  clearShipperHandler,
  dataTest,
  label,
  selectShipperHandler,
  shipper,
}: IShipperFilterButtonProps) => {
  const classes = useStyles()
  const [expandedState, setExpandedState] = useState<boolean>(false)
  const autocompleteRef = useRef<HTMLDivElement>(null)

  // handles expanding/unexpanding the autocomplete dropdown
  useEffect(() => {
    if (expandedState && autocompleteRef.current) {
      autocompleteRef.current.getElementsByTagName('input')[0].focus()
    }
  }, [expandedState])

  const [displayShipperFilter, setDisplayShipperFilter] = useState<boolean>(false)

  // loads the AutocompleteShipper component asynchronously to avoid the weird
  // state issues it causes on mounting
  useEffect(() => {
    setDisplayShipperFilter(true)
  }, [])

  const selectIcon = shipper ? (
    <>
      <div>
        <IconButton
          onClick={clearShipperHandler}
          Icon={CloseIcon}
          label='clear filter'
          data-test='clear-button'
          color='tertiary'
          size='small'
          noPadding
        />
      </div>
    </>
  ) : (
    <DownArrowIcon size='medium' />
  )

  return (
    <SelectBase
      applied={Boolean(shipper)}
      dataTest={dataTest}
      className={classes.parent}
      hideOnSelect
      autocomplete
      id={label}
      selectButtonType='filter-pill'
      stateCallback={setExpandedState}
      displayValue={
        <span className={classes.textContainer}>
          <b className={classes.text}>{shipper ? shipper.text : 'Shipper'}</b>
          {selectIcon}
        </span>
      }
      optionsLabel='Shipper'>
      <div className={classes.searchBox}>
        {displayShipperFilter && (
          <AutocompleteShipper
            onChange={selectShipperHandler}
            value={shipper}
            placeholder='Search'
          />
        )}
      </div>
    </SelectBase>
  )
}
